import { v4 } from 'uuid'

import { WebViewerInstance } from '@pdftron/webviewer'

import { FilterState } from '@modules/_shared/components/SelectFilters'

import { areDatesEqual } from '@utils/date'

import { Form, Observation, ProjectFile, Task } from '../../types'

import { Coordinates, Marker, MarkerType } from './type'

export const mapToMarker = (data: Partial<Observation | Form | Task>, type: MarkerType): Marker => {
  const position = data.projectFilePositions?.[0]

  return {
    id: data.id || v4(),
    type,
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    pageNumber: 1,
    data: data,
  }
}

const hasPosition = ({ projectFilePositions }: Observation | Form | Task): boolean =>
  (projectFilePositions?.length ?? 0) > 0

export const getMarkersByFilters = (
  markerType: MarkerType,
  plan: ProjectFile | undefined,
  filterFn: <T>(items: T[], filterFn: (item: T, filters: FilterState) => boolean) => T[],
): Marker[] => {
  if (!plan) return []

  if (markerType === 'observation') {
    return filterFn(plan.observations || [], (observation, filters) => {
      if (!hasPosition(observation)) return false

      if (filters.company && observation.company?.id !== filters.company.id) return false
      if (filters.trade && observation.trade.key !== filters.trade.key) return false
      if (filters.plan && observation.projectFile?.id !== filters.plan.id) return false
      if (filters.phase && observation.phase?.key !== filters.phase.key) return false
      if (filters.observationStatus && observation.status !== filters.observationStatus) return false
      if (filters.limitDate && !areDatesEqual(observation.limitDate, filters.limitDate)) return false
      if (filters.createdDate && !areDatesEqual(observation.createdDate, filters.createdDate)) return false

      return true
    }).map((observation) => mapToMarker(observation, 'observation'))
  }

  if (markerType === 'form') {
    return filterFn(plan.forms || [], (form, filters) => {
      if (!hasPosition(form)) return false

      if (filters.company && form.company?.id !== filters.company.id) return false
      if (filters.trade && form.trade.key !== filters.trade.key) return false
      if (filters.plan && form.projectFile?.id !== filters.plan.id) return false
      if (filters.phase && form.phase?.key !== filters.phase.key) return false
      if (filters.formStatus && form.status !== filters.formStatus) return false
      if (filters.limitDate && !areDatesEqual(form.limitDate, filters.limitDate)) return false
      if (filters.createdDate && !areDatesEqual(form.createdDate, filters.createdDate)) return false

      return true
    }).map((form) => mapToMarker(form, 'form'))
  }

  return filterFn(plan.tasks || [], (task, filters) => {
    if (!hasPosition(task)) return false

    if (filters.company && task.company?.id !== filters.company.id) return false
    if (filters.trade && task.trade.key !== filters.trade.key) return false
    if (filters.plan && task.projectFile?.id !== filters.plan.id) return false
    if (filters.taskStatus && task.status !== filters.taskStatus) return false
    if (filters.createdDate && !areDatesEqual(task.createdDate, filters.createdDate)) return false

    return true
  }).map((task) => mapToMarker(task, 'task'))
}

export const getCoordinatesFromEvent = (
  event: MouseEvent | TouchEvent,
  instanceRef: React.MutableRefObject<WebViewerInstance | null>,
  pageNumber: number,
): Coordinates | null => {
  if (!instanceRef.current) return null

  const { documentViewer } = instanceRef.current.Core
  const document = documentViewer.getDocument()

  const pdfCoordinates = documentViewer.getPDFCoordinatesFromMouseEvent(event as MouseEvent)
  const viewerCoordinates = document.getViewerCoordinates(pageNumber, pdfCoordinates.x, pdfCoordinates.y)

  const pageWidth = documentViewer.getPageWidth(pageNumber)
  const pageHeight = documentViewer.getPageHeight(pageNumber)
  const rotation = document.getPageRotation(pageNumber)

  let x, y

  switch (rotation) {
    case 90:
      x = ((pageHeight - viewerCoordinates.y) * 100) / pageHeight
      y = (viewerCoordinates.x * 100) / pageWidth
      break
    case 180:
      x = ((pageWidth - viewerCoordinates.x) * 100) / pageWidth
      y = ((pageHeight - viewerCoordinates.y) * 100) / pageHeight
      break
    case 270:
      x = (viewerCoordinates.y * 100) / pageHeight
      y = ((pageWidth - viewerCoordinates.x) * 100) / pageWidth
      break
    default:
      x = (viewerCoordinates.x * 100) / pageWidth
      y = (viewerCoordinates.y * 100) / pageHeight
  }

  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
  }
}
